// Formatting utilities

import type { Player } from '../types';

// Player name formatting
export const formatPlayerName = (player: Player): string => {
  return `${player.first_name} ${player.last_name}`;
};

export const formatPlayerNameWithNumber = (player: Player): string => {
  return `#${player.number} ${formatPlayerName(player)}`;
};

// Time formatting
export const formatTime = (time: string, format: '12h' | '24h' = '12h'): string => {
  if (!time) return '';
  
  try {
    const [hours, minutes] = time.split(':').map(Number);
    
    if (format === '24h') {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }
    
    // 12h format
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  } catch {
    return time;
  }
};

// Date formatting
export const formatDate = (date: string | Date): string => {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch {
    return String(date);
  }
};

export const formatDateShort = (date: string | Date): string => {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return String(date);
  }
};

// Duration formatting
export const formatDuration = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes}m`;
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  
  return `${hours}h ${remainingMinutes}m`;
};

// Position formatting
export const formatPosition = (position: string): string => {
  // Convert common abbreviations to full names
  const positionMap: Record<string, string> = {
    'QB': 'Quarterback',
    'RB': 'Running Back',
    'FB': 'Fullback',
    'WR': 'Wide Receiver',
    'TE': 'Tight End',
    'OL': 'Offensive Line',
    'C': 'Center',
    'G': 'Guard',
    'T': 'Tackle',
    'DL': 'Defensive Line',
    'DE': 'Defensive End',
    'DT': 'Defensive Tackle',
    'LB': 'Linebacker',
    'CB': 'Cornerback',
    'S': 'Safety',
    'FS': 'Free Safety',
    'SS': 'Strong Safety',
    'K': 'Kicker',
    'P': 'Punter',
    'LS': 'Long Snapper'
  };
  
  return positionMap[position] || position;
};

// Phone number formatting
export const formatPhoneNumber = (phone: string): string => {
  if (!phone) return '';
  
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');
  
  // Format as (XXX) XXX-XXXX for 10-digit US numbers
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  
  // Return original if not a standard format
  return phone;
};

// Weight formatting
export const formatWeight = (weight: number): string => {
  return `${weight} lbs`;
};

// Height formatting (assuming height is stored as string like "6'2\"")
export const formatHeight = (height: string): string => {
  return height;
};

// Capitalize first letter of each word
export const capitalizeWords = (str: string): string => {
  return str.replace(/\w\S*/g, (txt) => 
    txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  );
};

// Format team record (wins-losses)
export const formatRecord = (wins: number, losses: number): string => {
  return `${wins}-${losses}`;
};

// Format percentage
export const formatPercentage = (value: number, decimals: number = 1): string => {
  return `${(value * 100).toFixed(decimals)}%`;
};

// Format currency
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
}; 